import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useLayoutEffect,
  useRef,
} from 'react'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Applies `zoom` to the rendered scale (re-fitting on window resize in fit mode), and reports
 * whether fit-to-spread is currently possible.
 */
export function useFitToSpreadEffects({
  scrollRef,
  zoom,
  setScale,
  recomputeFitScaleAndAlign,
  fitContentDimensions,
  onFitToSpreadAvailabilityChange,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  zoom: number
  setScale: Dispatch<SetStateAction<number>>
  recomputeFitScaleAndAlign: () => void
  fitContentDimensions: { width: number; height: number }
  onFitToSpreadAvailabilityChange: CenterCanvasConfig['onFitToSpreadAvailabilityChange']
}) {
  const lastFitToSpreadAvailabilityRef = useRef<boolean | null>(null)

  // Compute scale for zoom / fit-to-page (layout effect so `scale` matches `zoom` before paint and before ruler zoom-anchor runs).
  useLayoutEffect(() => {
    const scroller = scrollRef.current

    if (!scroller) return

    const compute = () => {
      if (zoom === 0) {
        recomputeFitScaleAndAlign()
      } else {
        setScale(zoom)
      }
    }

    compute()

    if (zoom === 0) {
      // Window resize only — deliberately NOT a ResizeObserver on `scroller`. Side panels
      // (e.g. the left properties panel expanding on object selection) also resize the
      // scroller, and re-fitting the document to that transient width made the zoom level
      // (and ruler tick spacing/labels) visibly jump the moment a user selected an object,
      // even though they never asked to change zoom (WMPCM-8911). A real window resize still
      // refits via this listener; panel open/close no longer does.
      const onWinResize = () => compute()

      window.addEventListener('resize', onWinResize)

      return () => window.removeEventListener('resize', onWinResize)
    }
  }, [zoom, recomputeFitScaleAndAlign, scrollRef, setScale])

  useLayoutEffect(() => {
    const cb = onFitToSpreadAvailabilityChange

    if (!cb) return

    const scroller = scrollRef.current

    if (!scroller) return

    const { width: docW, height: docH } = fitContentDimensions

    const publish = () => {
      const ok = scroller.clientWidth >= 2 && scroller.clientHeight >= 2 && docW > 0 && docH > 0

      if (lastFitToSpreadAvailabilityRef.current !== ok) {
        lastFitToSpreadAvailabilityRef.current = ok
        cb(ok)
      }
    }

    publish()

    const ro = new ResizeObserver(() => publish())

    ro.observe(scroller)

    const onWin = () => publish()

    window.addEventListener('resize', onWin)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', onWin)
    }
  }, [onFitToSpreadAvailabilityChange, fitContentDimensions, scrollRef])
}
